import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
	PlusIcon,
	MagnifyingGlassIcon,
	ClockIcon,
	ArrowPathIcon,
	TrashIcon,
	CalendarIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';

import { MainContent, PageHeading } from '../../components/Shell/Content';
import {
	List,
	Badge,
	DateRangePicker,
	StatusBadge,
} from '../../components/nextgen';
import { AvatarGenerated } from '../../components/Avatar';
import {
	classNames,
	getUserFullName,
} from '../../components/helpers/functions';

import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';
import useSearchParams from '../../hooks/useSearchParams';

import {
	getTasks,
	updateTask,
	deleteTask,
	getRecurrenceRules,
	updateRecurrenceRule,
	deleteRecurrenceRule,
	generateRecurrenceRuleTask,
} from '../../api/tasks';
import { getTeam } from '../../api/company';
import { getApprovedSops } from '../../api/sops';
import {
	PauseIcon,
	PlayIcon,
	PencilIcon,
	ListBulletIcon,
} from '@heroicons/react/24/outline';
import EllipsisDropdown, {
	EllipsisDropdownItem,
} from '../../components/EllipsisDropdown';

// Recurrence labels
const RECURRENCE_LABELS = {
	daily: 'Daily',
	weekly: 'Weekly',
	biweekly: 'Biweekly',
	monthly: 'Monthly',
	quarterly: 'Quarterly',
	biyearly: 'Bi-yearly',
	yearly: 'Yearly',
};

const Tasks = () => {
	const navigate = useNavigate();

	const { setOpen: openCreateTask } = useModal('createTask');
	const { setOpen: openTaskDetail } = useModal('taskDetail');
	const { setOpen: openDeleteModal } = useModal('deleteModal');
	const { setOpen: openEditRecurrenceRule } = useModal('editRecurrenceRule');
	const { setOpen: openRecurrenceRuleHistory } = useModal(
		'recurrenceRuleHistory',
	);

	// URL search params for filters
	const {
		status: statusParam,
		assignedTo: assignedToParam,
		search: searchParam,
		assetId: assetIdParam,
	} = useSearchParams();

	// Local state for filters
	const [statusFilter, setStatusFilter] = useState(statusParam || 'open');
	const [assignmentFilter, setAssignmentFilter] = useState(
		assignedToParam || 'all',
	);
	const [searchQuery, setSearchQuery] = useState(searchParam || '');
	// Date filter - default to last month for completed/all views
	const [datePreset, setDatePreset] = useState('month');
	const [startDate, setStartDate] = useState(
		moment().subtract(1, 'month').format('YYYY-MM-DD'),
	);
	const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));

	// Date preset options
	const DATE_PRESETS = [
		{ value: 'week', label: 'Last Week', subtract: [1, 'week'] },
		{ value: 'month', label: 'Last Month', subtract: [1, 'month'] },
		{ value: '3months', label: 'Last 3 Months', subtract: [3, 'months'] },
		{ value: '6months', label: 'Last 6 Months', subtract: [6, 'months'] },
		{ value: '12months', label: 'Last 12 Months', subtract: [12, 'months'] },
	];

	// Handle date preset change
	const handleDatePresetChange = (preset) => {
		setDatePreset(preset);
		const presetConfig = DATE_PRESETS.find((p) => p.value === preset);
		if (presetConfig) {
			const [amount, unit] = presetConfig.subtract;
			setStartDate(moment().subtract(amount, unit).format('YYYY-MM-DD'));
			setEndDate(moment().format('YYYY-MM-DD'));
		}
	};

	// Fetch tasks
	const {
		value: tasksData,
		execute: refreshTasks,
		status: loadingStatus,
	} = useAsync(getTasks, { immediate: false });

	// Fetch team for assignment dropdown
	const { value: team } = useAsync(getTeam, {
		defaultPayload: { active: true },
	});

	// Fetch approved SOPs for task creation
	const { value: sopsData } = useAsync(getApprovedSops, {
		defaultPayload: {},
	});

	// Fetch recurrence rules
	const {
		value: rulesData,
		execute: refreshRules,
		status: rulesLoadingStatus,
	} = useAsync(getRecurrenceRules, {
		defaultPayload: { status: 'all', pageSize: 100 },
	});

	const rules = rulesData?.rows || [];
	const isRulesLoading = rulesLoadingStatus === 'pending';

	// Build task query params helper
	const getTaskParams = useCallback(() => {
		const params = {
			status: statusFilter,
			assignedTo: assignmentFilter,
			search: searchQuery,
			pageSize: 100,
		};
		if (statusFilter !== 'open') {
			params.startDate = startDate;
			params.endDate = endDate;
		}
		// Asset filter from URL (hidden filter)
		if (assetIdParam) {
			params.assetId = assetIdParam;
		}
		return params;
	}, [statusFilter, assignmentFilter, searchQuery, startDate, endDate, assetIdParam]);

	// Fetch tasks when filters change
	useEffect(() => {
		refreshTasks(getTaskParams());
	}, [getTaskParams]);

	// Update URL when filters change
	useEffect(() => {
		const params = new URLSearchParams();
		if (statusFilter !== 'open') params.set('status', statusFilter);
		if (assignmentFilter !== 'all') params.set('assignedTo', assignmentFilter);
		if (searchQuery) params.set('search', searchQuery);

		const search = params.toString();
		navigate({ search: search ? `?${search}` : '' }, { replace: true });
	}, [statusFilter, assignmentFilter, searchQuery, navigate]);

	// Handle task completion toggle
	const handleToggleComplete = useCallback(
		async (task, e) => {
			e?.stopPropagation();
			await updateTask(task.id, { completed: !task.completed });
			refreshTasks(getTaskParams());
		},
		[getTaskParams, refreshTasks],
	);

	// Handle task deletion
	const handleDeleteTask = useCallback(
		(task, e) => {
			e?.stopPropagation();
			openDeleteModal({
				title: 'Delete Task',
				description: `Are you sure you want to delete "${task.content}"? This action cannot be undone.`,
				confirmText: 'Delete',
				onConfirm: async () => {
					await deleteTask(task.id);
					refreshTasks(getTaskParams());
				},
			});
		},
		[openDeleteModal, getTaskParams, refreshTasks],
	);

	// Handle row click to open task detail
	const handleRowClick = useCallback(
		(task) => {
			console.log('sopsData: ', sopsData, task);

			openTaskDetail({
				task,
				team: team?.rows,
				sops: sopsData?.rows || [],
				onUpdate: async (taskId, updates) => {
					await updateTask(taskId, updates);
					refreshTasks(getTaskParams());
				},
				onToggle: async (taskId, currentCompleted) => {
					await updateTask(taskId, { completed: !currentCompleted });
					refreshTasks(getTaskParams());
				},
				onDelete: () => {
					refreshTasks(getTaskParams());
				},
				onSkip: () => {
					refreshTasks(getTaskParams());
				},
			});
		},
		[openTaskDetail, team, sopsData, getTaskParams, refreshTasks],
	);

	// Handle create task
	const handleCreateTask = useCallback(() => {
		openCreateTask({
			team: team?.rows,
			sops: sopsData || [],
			onSuccess: () => {
				refreshTasks(getTaskParams());
				refreshRules({ status: 'all', pageSize: 100 });
			},
		});
	}, [
		openCreateTask,
		team,
		sopsData,
		getTaskParams,
		refreshTasks,
		refreshRules,
	]);

	// Handle toggle recurrence rule active state
	const handleToggleRuleActive = useCallback(
		async (rule) => {
			try {
				await updateRecurrenceRule(rule.id, { active: !rule.active });
				refreshRules({ status: 'all', pageSize: 100 });
				refreshTasks(getTaskParams());
			} catch (err) {
				console.error('Error toggling rule:', err);
			}
		},
		[refreshRules, refreshTasks, getTaskParams],
	);

	// Handle delete recurrence rule
	const handleDeleteRule = useCallback(
		(rule) => {
			openDeleteModal({
				title: 'Delete Recurrence Rule',
				description: `Are you sure you want to delete the recurring task "${rule.content}"? This will stop all future occurrences. Existing tasks will not be deleted.`,
				confirmText: 'Delete',
				onConfirm: async () => {
					try {
						await deleteRecurrenceRule(rule.id);
						refreshRules({ status: 'all', pageSize: 100 });
						refreshTasks(getTaskParams());
					} catch (err) {
						console.error('Error deleting rule:', err);
					}
				},
			});
		},
		[openDeleteModal, refreshRules, refreshTasks, getTaskParams],
	);

	// Handle generate task now from recurrence rule
	const handleGenerateTaskNow = useCallback(
		async (rule) => {
			try {
				await generateRecurrenceRuleTask(rule.id);
				refreshRules({ status: 'all', pageSize: 100 });
				refreshTasks(getTaskParams());
			} catch (err) {
				console.error('Error generating task:', err);
			}
		},
		[refreshRules, refreshTasks, getTaskParams],
	);

	// Handle edit recurrence rule
	const handleEditRule = useCallback(
		(rule) => {
			openEditRecurrenceRule({
				rule,
				team: team?.rows,
				sops: sopsData?.rows || [],
				onSuccess: () => {
					refreshRules({ status: 'all', pageSize: 100 });
					refreshTasks(getTaskParams());
				},
				onDelete: () => {
					refreshRules({ status: 'all', pageSize: 100 });
					refreshTasks(getTaskParams());
				},
			});
		},
		[
			openEditRecurrenceRule,
			team,
			sopsData,
			refreshRules,
			refreshTasks,
			getTaskParams,
		],
	);

	// Handle view recurrence rule history
	const handleViewRuleHistory = useCallback(
		(rule) => {
			openRecurrenceRuleHistory({ rule });
		},
		[openRecurrenceRuleHistory],
	);

	const isLoading = loadingStatus === 'pending';
	const tasks = tasksData?.rows || [];

	// Render a single task item
	const renderTaskItem = (task) => {
		const dueDate = task.dueDate;
		// Use moment.utc to parse dates to avoid timezone shift
		const dueDateMoment = dueDate ? moment.utc(dueDate) : null;
		const todayStart = moment().startOf('day');
		const tomorrowStart = moment().add(1, 'day').startOf('day');

		const isOverdue =
			dueDateMoment && !task.completed && dueDateMoment.isBefore(todayStart);
		const isToday = dueDateMoment && dueDateMoment.isSame(todayStart, 'day');
		const isTomorrow =
			dueDateMoment && dueDateMoment.isSame(tomorrowStart, 'day');

		let dueDateLabel = dueDateMoment ? dueDateMoment.format('MMM D') : null;
		if (isToday) dueDateLabel = 'Today';
		if (isTomorrow) dueDateLabel = 'Tomorrow';

		console.log('task: ', task);

		return (
			<div className="flex items-center gap-3">
				{/* Completion checkbox */}
				<button
					type="button"
					onClick={(e) => handleToggleComplete(task, e)}
					className="flex-shrink-0"
				>
					{task.completed ? (
						<CheckCircleSolidIcon className="h-5 w-5 text-green-500" />
					) : (
						<div className="h-5 w-5 rounded-full border-2 border-gray-300 hover:border-indigo-500 transition-colors" />
					)}
				</button>

				{/* Task content */}
				<div className="flex-1 min-w-0">
					<div className="flex items-center gap-2">
						<p
							className={classNames(
								'text-sm font-medium truncate',
								task.completed ? 'text-gray-400 ' : 'text-gray-900',
							)}
						>
							{task.title || 'No title'}
						</p>
						{(task.recurrence || task.recurrenceRule?.recurrence) && (
							<ArrowPathIcon
								className="h-3.5 w-3.5 text-gray-400 flex-shrink-0"
								title={
									RECURRENCE_LABELS[
										task.recurrenceRule?.recurrence || task.recurrence
									]
								}
							/>
						)}
					</div>
					<div className="flex items-center gap-3 mt-1">
						{/* Linked entity */}
						{task.entityCode && (
							<span className="text-xs text-gray-500 font-mono">
								{task.entityCode}
							</span>
						)}
						{/* Due date */}
						{dueDateLabel && (
							<span
								className={classNames(
									'text-xs flex items-center gap-1',
									isOverdue ? 'text-red-600 font-medium' : 'text-gray-500',
								)}
							>
								<CalendarIcon className="h-3 w-3" />
								{dueDateLabel}
							</span>
						)}
						{/* Entity type badge */}
						{task.entityType && task.entityType !== 'standalone' && (
							<Badge isBeta size="xxs">
								{task.entityType === 'ticket' ? 'Ticket' : 'Work Order'}
							</Badge>
						)}
					</div>
				</div>

				{/* Assigned user */}
				{task.assignedToUser && (
					<div className="flex-shrink-0">
						<AvatarGenerated
							id={getUserFullName(task.assignedToUser)}
							size="xs"
							title={getUserFullName(task.assignedToUser)}
						/>
					</div>
				)}

				<StatusBadge
					status={
						task.skipped === true
							? 'skipped'
							: task.completed === true
								? 'completed'
								: 'open'
					}
				/>
			</div>
		);
	};

	return (
		<MainContent maxWidth="max-w-7xl">
			<PageHeading
				actions={[
					{
						name: 'Create Task',
						onClick: handleCreateTask,
						icon: PlusIcon,
					},
				]}
				divider={false}
			>
				Tasks
			</PageHeading>

			{/* Filters Bar */}
			<div className="flex flex-wrap items-center gap-4 mb-6">
				{/* Status Filter */}
				<div className="flex items-center rounded-lg border border-gray-200 p-0.5">
					{[
						{ value: 'open', label: 'Open' },
						{ value: 'completed', label: 'Completed' },
						{ value: 'all', label: 'All' },
					].map((option) => (
						<button
							key={option.value}
							onClick={() => setStatusFilter(option.value)}
							className={classNames(
								'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
								statusFilter === option.value
									? 'bg-indigo-600 text-white'
									: 'text-gray-600 hover:bg-gray-100',
							)}
						>
							{option.label}
						</button>
					))}
				</div>

				{/* Assignment Filter */}
				<div className="flex items-center rounded-lg border border-gray-200 p-0.5">
					{[
						{ value: 'all', label: 'All Tasks' },
						{ value: 'mine', label: 'Assigned to Me' },
					].map((option) => (
						<button
							key={option.value}
							onClick={() => setAssignmentFilter(option.value)}
							className={classNames(
								'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
								assignmentFilter === option.value
									? 'bg-indigo-600 text-white'
									: 'text-gray-600 hover:bg-gray-100',
							)}
						>
							{option.label}
						</button>
					))}
				</div>

				{/* Search */}
				<div className="relative flex-1 max-w-xs">
					<MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
					<input
						type="text"
						placeholder="Search tasks..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
					/>
				</div>

				{/* Count */}
				{tasksData && (
					<div className="text-sm text-gray-500 ml-auto">
						{tasksData.count} task{tasksData.count !== 1 ? 's' : ''}
					</div>
				)}
			</div>

			{/* Two-column layout */}
			<div className="flex  gap-6">
				{/* Left column - Tasks List */}
				<div className="flex-1 min-w-0">
					{/* Date Range Filter - only for completed/all views */}
					{statusFilter !== 'open' && (
						<div className="flex items-center gap-3 flex-1 mb-4">
							<CalendarIcon className="h-4 w-4 text-gray-400" />
							<select
								value={datePreset}
								onChange={(e) => handleDatePresetChange(e.target.value)}
								className="rounded-md border border-gray-300 py-1.5 pl-3 pr-8 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
							>
								{DATE_PRESETS.map((preset) => (
									<option key={preset.value} value={preset.value}>
										{preset.label}
									</option>
								))}
							</select>
							<DateRangePicker
								startDate={startDate}
								endDate={endDate}
								onStartDateChange={setStartDate}
								onEndDateChange={setEndDate}
								maxDate={moment().format('YYYY-MM-DD')}
							/>
						</div>
					)}

					<List
						data={tasks}
						keyField="id"
						loading={isLoading}
						renderItem={renderTaskItem}
						onItemClick={handleRowClick}
						itemClassName="group"
						emptyIcon={ClockIcon}
						emptyTitle={
							statusFilter === 'open'
								? 'All caught up!'
								: statusFilter === 'completed'
									? 'No completed tasks'
									: 'No tasks'
						}
						emptyDescription={
							statusFilter === 'open'
								? 'No open tasks. Create one to get started.'
								: statusFilter === 'completed'
									? 'Complete some tasks to see them here.'
									: 'Create a task to get started.'
						}
					/>

					{/* Create task button for empty state */}
					{!isLoading && tasks.length === 0 && (
						<div className="text-center mt-6">
							<button
								type="button"
								onClick={handleCreateTask}
								className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
							>
								<PlusIcon className="-ml-1 mr-2 h-5 w-5" />
								Create Task
							</button>
						</div>
					)}
				</div>

				{/* Right column - Recurring Tasks */}
				<div className="w-100 flex-shrink-0">
					<div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
						<div className="flex items-center gap-2 mb-4">
							<ArrowPathIcon className="h-5 w-5 text-gray-600" />
							<h3 className="text-sm font-semibold text-gray-900">
								Recurring Tasks
							</h3>
						</div>

						{isRulesLoading ? (
							<div className="flex items-center justify-center py-8">
								<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
							</div>
						) : rules.length === 0 ? (
							<div className="text-center py-6">
								<ArrowPathIcon className="mx-auto h-8 w-8 text-gray-300" />
								<p className="mt-2 text-sm text-gray-500">No recurring tasks</p>
								<p className="text-xs text-gray-400 mt-1">
									Create a task with recurrence to see it here
								</p>
							</div>
						) : (
							<div className="space-y-3">
								{rules.map((rule) => (
									<div
										key={rule.id}
										className={classNames(
											'border rounded-lg p-3 transition-colors',
											rule.active
												? 'border-gray-200 bg-white'
												: 'border-gray-100 bg-gray-100',
										)}
									>
										<div className="flex items-start justify-between gap-2">
											<div className="flex-1 min-w-0">
												<p
													className={classNames(
														'text-sm font-medium truncate',
														rule.active ? 'text-gray-900' : 'text-gray-500',
													)}
												>
													{rule.title || 'No title'}
												</p>
												<div className="flex items-center gap-2 mt-1.5">
													<Badge>
														<ArrowPathIcon className="h-3 w-3" />
														{RECURRENCE_LABELS[rule.recurrence]}
														{rule.nextOccurrence && rule.active ? (
															<>
																{' - Next: '}
																{moment(rule.nextOccurrence).format(
																	'MMM D, YYYY',
																)}
															</>
														) : (
															' - Paused'
														)}
													</Badge>
												</div>
											</div>
											<EllipsisDropdown>
												<EllipsisDropdownItem
													onClick={() => handleEditRule(rule)}
												>
													<span className="flex items-center gap-2">
														<PencilIcon className="h-4 w-4" />
														Edit
													</span>
												</EllipsisDropdownItem>
												<EllipsisDropdownItem
													onClick={() => handleViewRuleHistory(rule)}
												>
													<span className="flex items-center gap-2">
														<ListBulletIcon className="h-4 w-4" />
														History
													</span>
												</EllipsisDropdownItem>
												{rule.active && (
													<EllipsisDropdownItem
														onClick={() => handleGenerateTaskNow(rule)}
													>
														<span className="flex items-center gap-2">
															<PlusIcon className="h-4 w-4" />
															Generate Next
														</span>
													</EllipsisDropdownItem>
												)}
												<EllipsisDropdownItem
													onClick={() => handleToggleRuleActive(rule)}
												>
													<span className="flex items-center gap-2">
														{rule.active ? (
															<>
																<PauseIcon className="h-4 w-4" />
																Pause
															</>
														) : (
															<>
																<PlayIcon className="h-4 w-4" />
																Resume
															</>
														)}
													</span>
												</EllipsisDropdownItem>
												<EllipsisDropdownItem
													onClick={() => handleDeleteRule(rule)}
													danger
												>
													<span className="flex items-center gap-2">
														<TrashIcon className="h-4 w-4" />
														Delete
													</span>
												</EllipsisDropdownItem>
											</EllipsisDropdown>
										</div>
									</div>
								))}
							</div>
						)}
					</div>
				</div>
			</div>
		</MainContent>
	);
};

// Named export for backwards compatibility
export const TasksEntry = Tasks;

export default Tasks;
